import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { translations } from "@/i18n/translations";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider, useLang } from "@/context/LangContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Awakening from "@/pages/Awakening";
import Birth from "@/pages/Birth";
import Legal from "@/pages/Legal";
import SuperAdmin from "@/pages/SuperAdmin";
import MatchViewer from "@/pages/MatchViewer";
import { Footer } from "@/components/Footer";
import { PublicWall } from "@/components/PublicWall";
import { PricingTable } from "@/components/dashboard/PricingTable";
import { useFeatures } from "@/hooks/useFeatures";

const PRICING_HEAD = {
  en: { title: "Plans & Pricing", sub: "Choose the power level of your avatar. Upgrade anytime." },
  es: { title: "Planes y Precios", sub: "Elige el nivel de poder de tu avatar. Mejora cuando quieras." },
};

const Landing = () => {
  const { lang, setLang } = useLang();
  const t = translations[lang];
  const p = PRICING_HEAD[lang] || PRICING_HEAD.en;
  const navigate = useNavigate();
  const features = useFeatures();

  return (
    <main className="min-h-screen bg-[#05050A] text-white overflow-x-hidden">
      <Navbar lang={lang} setLang={setLang} />
      <Hero t={t.hero} matchmakingOn={!!features.matchmaking_enabled} />
      <PublicWall t={t.publicWall} />
      <section id="pricing" className="relative max-w-7xl mx-auto px-6 md:px-12 mt-8 pb-10" data-testid="landing-pricing-section">
        <h2 className="font-heading text-2xl md:text-3xl font-black tracking-tight">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B026FF] to-[#00F5FF]">{p.title}</span>
        </h2>
        <p className="text-base md:text-lg font-light text-[#A0A0B0] mt-3 mb-10 max-w-2xl">{p.sub}</p>
        <PricingTable lang={lang} onCta={() => navigate("/register")} />
      </section>
      <Footer lang={lang} />
    </main>
  );
};

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#05050A]" />;
  return user ? children : <Navigate to="/login" replace />;
};

function App() {
  return (
    <ErrorBoundary>
      <LangProvider>
        <AuthProvider>
          <Toaster theme="dark" position="top-center" richColors />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/legal/:doc" element={<Legal />} />
              <Route path="/superadmin" element={<Protected><SuperAdmin /></Protected>} />
              <Route path="/match/:id" element={<Protected><MatchViewer /></Protected>} />
              <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
              <Route path="/awakening" element={<Protected><Awakening /></Protected>} />
              <Route path="/birth" element={<Protected><Birth /></Protected>} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </LangProvider>
    </ErrorBoundary>
  );
}

export default App;
